import { useMutation } from '@tanstack/react-query'
import PhoneInput from 'components/Common/Form/Input/PhoneInput'
import Select from 'components/Common/Form/Input/Select'
import TextInput from 'components/Common/Form/Input/TextInput'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import TextButton from 'LuxKit/Button/TextButton'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { formToObject } from 'lib/forms/formToObject'
import { rem } from 'polished'
import type { FormEventHandler } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { agentHubOrSubBrandConfig } from 'agentHub/constants'
import type { ExpressionOfInterestData } from 'agentHub/api/requestPostExpressionOfInterest'
import requestPostExpressionOfInterest from 'agentHub/api/requestPostExpressionOfInterest'
import clsx from 'clsx'
import TextLink from 'LuxKit/TextLink'
import { connect } from 'react-redux'
import AgentHubAffiliationAutocomplete from 'agentHub/components/AgentHubAffiliationAutocomplete'
import { AGENT_HUB_EOI_REGION_CODES, AGENT_HUB_DEFAULT_PHONE_NUMBER_PREFIX, getAgentHubEoiRegionOptions } from 'constants/config/region'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { fetchAgentAffiliationOptions } from 'actions/AgentHubActions'
import getAgentHubPhonePlaceholder from 'agentHub/utils/getAgentHubPhonePlaceholder'
import { isAgentHubAffiliationRequired } from 'agentHub/selectors/agentHubSelectors'
import AgentHubAddressInputFields from 'agentHub/components/AgentHubAddressInputFields'
import { getAgencyCodeInfoFromCountryCode, sanitizeABN, validateBusinessIdentifier } from 'agentHub/pages/AgentHubAccountSetup/utils/agencyCodeUtils'
import { isAgencyIdentifierRequired } from 'lib/agentHub/agentHubUtils'
import { getHonourificTitlesForLocale } from 'constants/titles'

const InputGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr;
    grid-template-areas:"agencyName agencyName"
                        "regionCode regionCode"
                        "title title"
                        "businessCodeValue businessCodeValue"
                        "address address"
                        "affiliation affiliation"
                        "firstName lastName"
                        "phone phone"
                        "referrerEmail referrerEmail"
                        "email email";
    &.showSpecifyAffiliation {
      grid-template-areas:"agencyName agencyName"
                        "regionCode regionCode"
                        "affiliation affiliation"
                        "title title"
                        "businessCodeValue businessCodeValue"
                        "address address"
                        "specifyAffiliation specifyAffiliation"
                        "firstName lastName"
                        "phone phone"
                        "referrerEmail referrerEmail"
                        "email email";
    }
    .grid-agencyName {
      grid-area: agencyName;
    }
    .grid-regionCode {
      grid-area: regionCode;
    }
    .grid-title { grid-area: title; }
    .grid-businessCodeValue { grid-area: businessCodeValue; }
    .grid-address { grid-area: address; }
    .grid-firstName {
      grid-area: firstName;
    }
    .grid-lastName{
      grid-area: lastName;
    }
    .grid-phone {
      grid-area: phone;
    }
    .grid-referrerEmail {
      grid-area: referrerEmail;
    }
    .grid-email {
      grid-area: email;
    }
    .grid-affiliation {
      grid-area: affiliation;
    }
    .grid-specifyAffiliation {
      grid-area: specifyAffiliation;
    }
  }
`

export interface FormData {
  agencyName: string
  firstName: string
  lastName: string
  email: string
  affiliationId: string
  phone: { phone: string, prefix: string }
  specifyAffiliation?: string
  regionCode: App.AgentHubEoiRegions
  referrerEmail?: string
  title: App.Title
  businessCodeValue?: string
  streetAddress?: string
  city?: string
  stateOrTerritory?: string
  postcode?: string
  country?: string
}

function mapFormData(formData: FormData): ExpressionOfInterestData {
  const businessCodeType = getAgencyCodeInfoFromCountryCode(formData.regionCode).label

  return {
    agencyName: formData.agencyName,
    givenName: formData.firstName,
    surname: formData.lastName,
    email: formData.email,
    affiliationId: formData.affiliationId,
    phone: `${formData.phone.prefix} ${formData.phone.phone}`,
    regionCode: formData.regionCode,
    title: formData.title,
    ...(formData.businessCodeValue && {
      ...(businessCodeType && { businessCodeType }),
      businessCodeValue: formData.regionCode === 'AU'
        ? sanitizeABN(formData.businessCodeValue)
        : formData.businessCodeValue,
    }),
    ...(formData.streetAddress && { streetAddress: formData.streetAddress }),
    ...(formData.city && { city: formData.city }),
    ...(formData.stateOrTerritory && { stateOrTerritory: formData.stateOrTerritory }),
    ...(formData.postcode && { postcode: formData.postcode }),
    ...(formData.country && { country: formData.country }),
    ...(formData.referrerEmail && { referrerEmail: formData.referrerEmail }),
  }
}

interface Props {
  geoRegion: string
}

function AgentHubExpressionOfInterestModal({
  geoRegion,
}: Props) {
  const dispatch = useAppDispatch()
  const intl = useIntl()

  const initialRegionCode = (AGENT_HUB_EOI_REGION_CODES as ReadonlyArray<string>).includes(geoRegion)
    ? geoRegion as App.AgentHubEoiRegions
    : 'AU'
  const [regionCode, setRegionCode] = useState<App.AgentHubEoiRegions>(initialRegionCode)
  const regionOptions = useMemo(() => getAgentHubEoiRegionOptions(intl.locale), [intl.locale])
  const titleOptions = useMemo(() => getHonourificTitlesForLocale(intl.locale, ['Mr', 'Mrs', 'Ms', 'Mx', 'Dr']), [intl.locale])
  const agencyCode = getAgencyCodeInfoFromCountryCode(regionCode)
  const agentTitleLabel = intl.formatMessage({
    id: 'dAcMuOOfWWOEHLpkphr2',
    defaultMessage: 'Agent title',
  })
  const selectTitlePlaceholder = intl.formatMessage({
    id: 'Lc_3i3k3lOPEBRV7Kpm7',
    defaultMessage: 'Select title',
  })
  const agentTitleRequiredMessage = intl.formatMessage({
    id: 'kd7x_SIhyBS3oIRpYxMi',
    defaultMessage: 'Agent title is required',
  })
  const businessRegistrationFallbackLabel = intl.formatMessage({
    id: 'VS4gtjx54ctJcJxRR7a6',
    defaultMessage: 'Business registration number',
  })
  const businessRegistrationLabel = agencyCode.label || businessRegistrationFallbackLabel
  const businessRegistrationRequiredMessage = intl.formatMessage({
    id: 'GUtteYeQT0m9bVzvknDK',
    defaultMessage: '{label} is required',
  }, { label: businessRegistrationLabel })
  const businessRegistrationInvalidMessage = intl.formatMessage({
    id: 'Z5A3iXFWNm4dEwZE027K',
    defaultMessage: 'Please enter a valid {label}',
  }, { label: businessRegistrationLabel })
  const [isError, setIsError] = useState(false)
  const businessCodeInputRef = useRef<HTMLInputElement>(null)
  const [errorMsg, setErrorMsg] = useState<string>('')
  const [finished, setFinished] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [shouldDisplayAffiliationInput, setShouldDisplayAffiliationInput] = useState(false)
  const emailSendMutation = useMutation({
    mutationFn: async(userData: ExpressionOfInterestData) => {
      return requestPostExpressionOfInterest(userData)
    },
    onSuccess: async() => {
      setFinished(true)
      setIsError(false)
    },
    onError: async(error: App.ApiCallFailure) => {
      setErrorMsg(error.message)
      setIsError(true)
      setIsSubmitted(false)
    },
  })
  const fetching = useAppSelector((state) => state.agentHub.affiliationOptions.fetching)
  const affiliationOptions = useAppSelector((state) =>
    isAgentHubAffiliationRequired
      ? state.agentHub.affiliationOptions.options[regionCode]
      : [],
  )

  const handleSubmit = useCallback<FormEventHandler<HTMLFormElement>>((e) => {
    e.preventDefault()
    const formData = formToObject<FormData>(e.currentTarget)
    businessCodeInputRef.current?.setCustomValidity('')
    if (
      agencyCode.label &&
      formData.businessCodeValue &&
      !validateBusinessIdentifier(regionCode, formData.businessCodeValue)
    ) {
      businessCodeInputRef.current?.setCustomValidity(businessRegistrationInvalidMessage)
      e.currentTarget.reportValidity()
      return
    }
    setIsSubmitted(true)
    const mappedFormData = mapFormData(formData)
    emailSendMutation.mutate(mappedFormData)
  }, [agencyCode.label, businessRegistrationInvalidMessage, emailSendMutation, regionCode])

  function handleAffiliationSelection(value: string) {
    if (value === 'Other (please specify)') {
      setShouldDisplayAffiliationInput(true)
      return
    }

    setShouldDisplayAffiliationInput(false)
  }

  useEffect(() => {
    if (isAgentHubAffiliationRequired) {
      dispatch(fetchAgentAffiliationOptions(regionCode))
    }
  }, [regionCode, dispatch])

  const thankYouTitle = intl.formatMessage({
    id: 'GI0UW3UqUsxRWB6ypNPn',
    defaultMessage: 'Thank you!',
  })
  const registerTitle = intl.formatMessage({
    id: 'qbRDiPubLnTRcjsNNoT7',
    defaultMessage: 'Register your interest',
  })
  const nameInvalidErrorMessage = intl.formatMessage({
    id: 'MPjVfmxwAQ0K0jPqIPKZ',
    defaultMessage: 'Invalid characters. Please use only letters and spaces',
  })
  const emailInvalidErrorMessage = intl.formatMessage({
    id: 'fBQ1bwVmgLwIst8DngYa',
    defaultMessage: 'Invalid email address',
  })

  return (<Modal size={finished ? 'S' : 'L'} title={finished ? thankYouTitle : registerTitle}>
    {isError && <MessageBanner kind="critical">
      {intl.formatMessage({
        id: 'ihYadzhJgw93Bn-FHUqB',
        defaultMessage: 'Something went wrong while setting up your registration. Error message: {error}',
      }, { error: errorMsg })}
    </MessageBanner>}
    {finished && <BodyText variant="medium">
      {intl.formatMessage({
        id: 'Bf2ObLNlL9eglAEePq0c',
        defaultMessage: 'You have officially registered to join {name}. We will keep you notified with launch updates and next steps to verify your account.',
      }, { name: agentHubOrSubBrandConfig.displayShortName })}
    </BodyText>}
    {!finished && (<Group direction="vertical" gap={20}>
      <BodyText variant="medium">
        {intl.formatMessage({
          id: 'BGKVQS_WNydp50MFkVUf',
          defaultMessage: 'Join {name} to earn and unlock exclusive travel deals',
        }, { name: agentHubOrSubBrandConfig.displayShortName })}
      </BodyText>

      <form
        onSubmit={handleSubmit}
        name="agentHubExpressionOfInterestForm"
      >
        <InputGrid className={clsx({ showSpecifyAffiliation: shouldDisplayAffiliationInput })}>
          <TextInput
            className="grid-agencyName"
            label={intl.formatMessage({
              id: 'JV42CqtTy8BMtK7nxYLO',
              defaultMessage: 'Agency name',
            })}
            maxLength={100}
            minLength={2}
            name="agencyName"
            placeholder={intl.formatMessage({
              id: 'P7NE26P18dXDoRV-mh2h',
              defaultMessage: 'Agency name',
            })}
            required
            requiredErrorMessage={intl.formatMessage({
              id: 'm6kVBRCBE7PMzX4g6aYT',
              defaultMessage: 'Agency name is required',
            })}
          />
          <Select
            className="grid-regionCode"
            label={intl.formatMessage({
              id: 'dvnWIWrG-yYZhHZsOr7o',
              defaultMessage: 'Region',
            })}
            name="regionCode"
            placeholder={intl.formatMessage({
              id: 'iTS9NnxcJgD6i4tm6_ZX',
              defaultMessage: 'Region',
            })}
            required
            requiredErrorMessage={intl.formatMessage({
              id: 'DbPjsRQd9r_0DQRUZul0',
              defaultMessage: 'Region is required',
            })}
            value={regionCode}
            onChange={(e) => setRegionCode(e.target.value as App.AgentHubEoiRegions)}
          >
            {regionOptions.map((item) => <option key={item.code} value={item.code}>{item.name}</option>)}
          </Select>
          {isAgentHubAffiliationRequired && (
            <div className="grid-affiliation">
              <AgentHubAffiliationAutocomplete
                affiliationOptions={affiliationOptions}
                loading={fetching}
                onSelect={(value) => handleAffiliationSelection(value)}
              />
            </div>
          )}
          <Select className="grid-title" label={agentTitleLabel} name="title" required requiredErrorMessage={agentTitleRequiredMessage}>
            <option value="">{selectTitlePlaceholder}</option>
            {titleOptions.map(title => <option key={title} value={title}>{title}</option>)}
          </Select>
          <TextInput
            className="grid-businessCodeValue"
            label={businessRegistrationLabel}
            name="businessCodeValue"
            required={isAgencyIdentifierRequired(regionCode)}
            requiredErrorMessage={businessRegistrationRequiredMessage}
            invalidErrorMessage={businessRegistrationInvalidMessage}
            ref={businessCodeInputRef}
            onChange={() => {
              const input = businessCodeInputRef.current
              if (input && regionCode === 'AU') {
                const digits = sanitizeABN(input.value).slice(0, 11)
                if (digits !== input.value) {
                  input.value = digits
                }
              }
              input?.setCustomValidity('')
            }}
          />
          <div className="grid-address"><AgentHubAddressInputFields /></div>
          {shouldDisplayAffiliationInput &&
            <TextInput
              className="grid-specifyAffiliation"
              invalidErrorMessage={nameInvalidErrorMessage}
              label={intl.formatMessage({
                id: 's-yEHWHaeny9Qr4RwB-7',
                defaultMessage: 'Please specify affiliation',
              })}
              maxLength={100}
              minLength={2}
              name="specifyAffiliation"
              pattern="^[A-Za-z ]+$"
              placeholder={intl.formatMessage({
                id: 'MOq0g9oOUnemlARjUcMt',
                defaultMessage: 'Affiliation',
              })}
              required={Boolean(shouldDisplayAffiliationInput)}
              requiredErrorMessage={intl.formatMessage({
                id: 'Xv8penG_nz0jpoueGrJx',
                defaultMessage: 'Affiliation is required',
              })}
            />}

          <TextInput
            className="grid-firstName"
            invalidErrorMessage={nameInvalidErrorMessage}
            label={intl.formatMessage({
              id: '8kM8-MYASivjkqLSLY8V',
              defaultMessage: 'First name',
            })}
            maxLength={100}
            minLength={2}
            name="firstName"
            pattern="^[A-Za-z ]+$"
            placeholder={intl.formatMessage({
              id: 'peVYSkwLaAeIiE_MJDh4',
              defaultMessage: 'First name',
            })}
            required
            requiredErrorMessage={intl.formatMessage({
              id: 'Uh5nSfO8ANx4-9Rroa-p',
              defaultMessage: 'First name is required',
            })}
          />

          <TextInput
            className="grid-lastName"
            invalidErrorMessage={nameInvalidErrorMessage}
            label={intl.formatMessage({
              id: 'V--2oZtuQA6qU6J3qRwl',
              defaultMessage: 'Last name',
            })}
            maxLength={100}
            minLength={2}
            name="lastName"
            pattern="^[A-Za-z ]+$"
            placeholder={intl.formatMessage({
              id: 'rhLLwFARzDoGkD02mfMz',
              defaultMessage: 'Last name',
            })}
            required
            requiredErrorMessage={intl.formatMessage({
              id: 'BBnSeC-4_A3wWe9pT0VX',
              defaultMessage: 'Last name is required',
            })}
          />

          <PhoneInput
            allowLandline
            useStrictValidation
            className="grid-phone"
            inputTestId="eoi-phone-input"
            prefixCountry={regionCode}
            defaultPhonePrefix={AGENT_HUB_DEFAULT_PHONE_NUMBER_PREFIX[regionCode]}
            label={intl.formatMessage({
              id: 'tu7pFTZeLt59IjqOMF0b',
              defaultMessage: 'Phone number',
            })}
            name="phone"
            key={regionCode}
            placeholder={getAgentHubPhonePlaceholder(intl, regionCode)}
            required
            requiredErrorMessage={intl.formatMessage({
              id: 'ykqPpjxvRbZMwiRj9dMk',
              defaultMessage: 'Phone number is required',
            })}
          />

          <TextInput
            className="grid-referrerEmail"
            helpText={intl.formatMessage({
              id: 'DRHsurpRGJzvYjzeFMQe',
              defaultMessage: 'Enter the email address of the travel agent who referred you to Agent Hub.',
            })}
            invalidErrorMessage={emailInvalidErrorMessage}
            label={intl.formatMessage({
              id: 'sACCGHCp7PP1ZYNGB8JH',
              defaultMessage: 'Referrer’s email address',
            })}
            maxLength={100}
            name="referrerEmail"
            type="email"
          />

          <TextInput
            className="grid-email"
            invalidErrorMessage={emailInvalidErrorMessage}
            label={intl.formatMessage({
              id: '44FrARIAtpMgz-04hFkp',
              defaultMessage: 'Email address',
            })}
            maxLength={100}
            name="email"
            placeholder={intl.formatMessage({
              id: 'XhliSXVZRw7s1uDgJoES',
              defaultMessage: 'Enter your agency email address',
            })}
            required
            requiredErrorMessage={intl.formatMessage({
              id: '8iiJVCJAhuwbBL7JzP_H',
              defaultMessage: 'Email address is required',
            })}
            type="email"
          />
        </InputGrid>
        <Group tabletGap="0 32" gap="16 0" tabletDirection="horizontal" direction="vertical" tabletHorizontalAlign="space-evenly" horizontalAlign="stretch">
          <BodyText variant="medium">
            <FormattedMessage
              id="hPQfE4MRqU4ypQhEX-y8"
              defaultMessage="By continuing, you agree to our <termsLink>Terms & Conditions</termsLink> and <privacyLink>Privacy Policy</privacyLink>, and to subscribe to marketing communications for offers, alerts and services."
              
              values={{
                termsLink: (chunks: React.ReactNode) => <TextLink key="terms-link" href={agentHubOrSubBrandConfig.termsUrl} rel="noopener noreferrer" target="_blank" weight="regular">{chunks}</TextLink>,
                privacyLink: (chunks: React.ReactNode) => <TextLink key="privacy-link" href={agentHubOrSubBrandConfig.policyUrl} rel="noopener noreferrer" target="_blank" weight="regular">{chunks}</TextLink>,
              }}
            />
          </BodyText>
          <TextButton disabled={isSubmitted} fit="mobile-full-width" kind="primary" size="large" type="submit">
            {intl.formatMessage({
              id: 'U8J6VtIiO9YXsx0wAERx',
              defaultMessage: 'Submit',
            })}
          </TextButton>
        </Group>
      </form>
    </Group>)}
  </Modal>)
}

const mapStateToProps = (state: App.State) => ({
  geoRegion: state.geo.currentRegionCode,
})

export default connect(mapStateToProps)(AgentHubExpressionOfInterestModal)
